import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { ApiError } from '@/services/api';
import s from './ui.module.css';

export { s as ui };

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';

export function Button({
  variant = 'primary',
  size,
  className,
  type = 'button',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' }) {
  return (
    <button
      {...props}
      type={type}
      className={[s.btn, s[variant], size ? s.sm : '', className].filter(Boolean).join(' ')}
    />
  );
}

export function ButtonLink({
  variant = 'secondary',
  className,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & { variant?: Variant }) {
  return (
    <a
      target="_blank"
      rel="noreferrer"
      {...props}
      className={[s.btn, s[variant], className].filter(Boolean).join(' ')}
    />
  );
}

export function Card({
  title,
  actions,
  children,
  highlight,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  highlight?: boolean;
}) {
  return (
    <section className={highlight ? `${s.card} ${s.cardHighlight}` : s.card}>
      {title || actions ? (
        <div className={s.cardHead}>
          {title ? <h2>{title}</h2> : <span />}
          {actions}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function Badge({
  tone,
  children,
}: {
  tone?: 'green' | 'amber' | 'red';
  children: ReactNode;
}) {
  const toneClass =
    tone === 'green'
      ? s.badgeGreen
      : tone === 'amber'
        ? s.badgeAmber
        : tone === 'red'
          ? s.badgeRed
          : '';
  return <span className={`${s.badge} ${toneClass}`}>{children}</span>;
}

const STAR = 'M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3 6.1 20.6l1.3-6.6L2.5 9.4l6.6-.8z';

export function Meter({ score }: { score: number }) {
  const on = Math.round(score);
  return (
    <span className={s.meter} role="img" aria-label={`${score} out of 5`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <svg key={i} viewBox="0 0 24 24" aria-hidden="true" className={i < on ? s.on : undefined}>
          <path d={STAR} />
        </svg>
      ))}
    </span>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className={s.center} role="status">
      <span className={s.spinner} />
      <span className="sr-only">{label}</span>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className={s.empty}>{children}</p>;
}

export function errorText(err: unknown): string {
  if (err instanceof ApiError)
    return err.details.length ? err.details.map((d) => d.message).join(' ') : err.message;
  return 'Something went wrong. Please try again.';
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p className={s.error} role="alert">
      {errorText(error)}
    </p>
  );
}

export function LoadMore({
  hasMore,
  loading,
  onClick,
}: {
  hasMore: boolean;
  loading: boolean;
  onClick: () => void;
}) {
  if (!hasMore) return null;
  return (
    <div className={`${s.row} ${s.centerRow}`}>
      <Button variant="secondary" onClick={onClick} disabled={loading}>
        {loading ? 'Loading…' : 'Load more'}
      </Button>
    </div>
  );
}

export const dateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';
export const date = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—';
